import { isDevOnlyPagesVisible } from '@/composables/useDevOnlyOverride';

// Paths (and their sub-paths) the router guard hard-blocks outside development, see the
// "Dev-only routes" check in the guard in router/index.js.
export const devOnlyPaths = [
  '/inventory/awd-shipments', // AWD shipment datasets are development-only.
  '/inventory/fba-shipments', // FBA inbound datasets are development-only.
  '/catalog', // Catalog products — inventory backend is dev-only for M1
  '/ppc/management', // Management v2
  '/ppc/dayparting', // moved under Dev Tools (WP-667)
  '/ppc/traffic-conversion-v2', // Traffic & Conversion (Legacy)
  '/ppc/unified-report-v2', // Advertised Product (Legacy)
  '/settings/data-status', // ETL data status — dev-only for now
  '/settings/connections-hub', // Connections hub (+ /:provider detail pages) — dev-only until it replaces /settings/connections
  '/brand-analytics/brand-metrics', // Amazon Brand Metrics — dev-only; exact path, never the /brand-analytics prefix (released pages)
  '/integrations/ebay', // eBay connect test page (+ /error) — dev-only until the eBay launch
  '/integrations/shopify', // Shopify connections (+ /claim) — dev-only until the Shopify launch
  '/integrations/walmart', // Walmart connections — dev-only, Walmart is hidden in prod
  '/integrations/google', // Google connections (+ /connected, /error) — dev-only until the Google launch
  '/integrations/tiktok', // TikTok Ads connections (+ /connected, /error) — dev-only until the TikTok launch
  '/integrations/pinterest', // Pinterest connections (+ /connected, /error) — dev-only until the Pinterest launch
  '/integrations/meta', // Meta Ads connections (+ /connected, /error) — dev-only until the Meta launch
  '/integrations/bing', // Microsoft Advertising connections (+ /connected, /error) — dev-only until the Bing launch
  '/ebay', // eBay analytics pages — dev-only until the eBay launch
  '/shopify', // Shopify analytics pages — dev-only until the Shopify launch
  '/walmart', // Walmart analytics pages — dev-only, Walmart is hidden in prod
  '/meta', // Meta Ads analytics pages — dev-only until the Meta launch (the connect pages are /integrations/meta)
  '/settings/webhooks', // Webhooks — dev-only, its backend isn't on prod yet
  '/business-reports/promotions',
  '/business-reports/seller-feedback', // Seller Feedback (+ tabs) — dev-only again (owner, 2026-10-14)
  '/ppc/search-term-asins', // Search Term ASINs — dev-only until its olap fields (rank, top-N, ownership) reach olap main
];

// A path (or a sub-path of one) listed in devOnlyPaths.
export const isDevOnlyPath = (path) => {
  const normalizedPath = String(path || '').toLowerCase();
  return devOnlyPaths.some((p) => normalizedPath === p || normalizedPath.startsWith(`${p}/`));
};

// The single definition of "this dev-only path may be opened": any non-dev-only path, or
// isDevOnlyPagesVisible (Dev Settings -> "Show dev-only pages": on by default in development,
// super admins only elsewhere; mirrors useNavItemVisibility). Shared by the router guard and by UI
// that offers a link or an option only where the destination pages are allowed.
export const isDevOnlyPathAllowed = (path, authStore, envStore) =>
  !isDevOnlyPath(path) || isDevOnlyPagesVisible(authStore, envStore);

// The eBay connect and analytics pages may be opened (the dev-only pages are shown, see
// isDevOnlyPagesVisible). Offers that lead into eBay - the onboarding choice, the eBay-only
// landing - are shown only where this holds, so a user is never sent to a route the guard would block.
const EBAY_PAGE_PATHS = ['/integrations/ebay', '/ebay'];
export const isEbayPagesAllowed = (authStore, envStore) =>
  EBAY_PAGE_PATHS.every((path) => isDevOnlyPathAllowed(path, authStore, envStore));
